import {isFeatureEnabled} from '@github-ui/feature-flags'
import {useState, type Dispatch, type SetStateAction} from 'react'
import type {PreparedPrompt} from '@github-ui/ahp-session-client/contracts'

import {isEnvironmentSelectionStale} from '../components/new-task-chat-input/environment-strip/environment-availability'
import type {EnvironmentOption} from '../components/new-task-chat-input/environment-strip/environment-option'
import type {Environment} from '../types/environment'
import type {McpPermissionMode} from '../types/api-task'
import {useEnvironments} from './use-environments'

export type EnvironmentSubmitInput = {
  environmentId: string
  prompt: PreparedPrompt
  repositoryNwo?: string
  baseRef?: string
  managedSandbox?: boolean
  /**
   * MCP permission mode to seed on the created session's first turn. Only
   * carried by the Sandbox composer today; the coding-agent API path uses its
   * own `permission_mode` field on `useCreateAPITask`.
   */
  mcpPermissionMode?: McpPermissionMode
}

export type EnvironmentSubmit = (input: EnvironmentSubmitInput) => Promise<unknown>

type UseEnvironmentSelectionOptions = {
  onEnvironmentSubmit?: EnvironmentSubmit
  supportsManagedSandbox?: boolean
  /**
   * Optional initial environment. Used when a caller needs the composer to
   * mount with a pre-selected environment (e.g. restoring the Sandbox selection
   * after the ScopePicker's SSO round-trip). Only honored on first render.
   */
  initialEnvironment?: EnvironmentOption | null
}

type UseEnvironmentSelectionResult = {
  selectedEnvironment: EnvironmentOption | null
  setSelectedEnvironment: Dispatch<SetStateAction<EnvironmentOption | null>>
  availableEnvironments: Environment[] | undefined
  isAhpEnvironment: boolean
  isSandboxEnvironment: boolean
  showSandboxOption: boolean
  showEnvironmentPicker: boolean
  isEnvironmentsEnabled: boolean
}

export function isSandboxOptionAvailable({
  isEnvironmentsEnabled,
  isManagedSandboxEnabled,
  supportsManagedSandbox,
  hasEnvironmentSubmit,
}: {
  isEnvironmentsEnabled: boolean
  isManagedSandboxEnabled: boolean
  supportsManagedSandbox: boolean
  hasEnvironmentSubmit: boolean
}): boolean {
  return isEnvironmentsEnabled && isManagedSandboxEnabled && supportsManagedSandbox && hasEnvironmentSubmit
}

export function useEnvironmentSelection({
  onEnvironmentSubmit,
  supportsManagedSandbox = false,
  initialEnvironment = null,
}: UseEnvironmentSelectionOptions): UseEnvironmentSelectionResult {
  const [selectedEnvironment, setSelectedEnvironment] = useState<EnvironmentOption | null>(initialEnvironment)
  const isEnvironmentsEnabled = isFeatureEnabled('copilot_mission_control_environments_web_kickoff')
  const isManagedSandboxEnabled = isFeatureEnabled('copilot_mission_control_managed_sandbox_environments')
  const {data: availableEnvironments} = useEnvironments({enabled: isEnvironmentsEnabled && !!onEnvironmentSubmit})

  if (isEnvironmentSelectionStale(selectedEnvironment, availableEnvironments)) {
    setSelectedEnvironment(null)
  }

  const isAhpEnvironment =
    isEnvironmentsEnabled && (selectedEnvironment?.kind === 'ahp' || selectedEnvironment?.kind === 'sandbox')
  const isSandboxEnvironment = isEnvironmentsEnabled && selectedEnvironment?.kind === 'sandbox'
  const showSandboxOption = isSandboxOptionAvailable({
    isEnvironmentsEnabled,
    isManagedSandboxEnabled,
    supportsManagedSandbox,
    hasEnvironmentSubmit: !!onEnvironmentSubmit,
  })
  const showEnvironmentPicker = isEnvironmentsEnabled && !!onEnvironmentSubmit

  return {
    selectedEnvironment,
    setSelectedEnvironment,
    availableEnvironments,
    isAhpEnvironment,
    isSandboxEnvironment,
    showSandboxOption,
    showEnvironmentPicker,
    isEnvironmentsEnabled,
  }
}
